'use client';

import type { UseInfiniteQueryResult, UseSuspenseInfiniteQueryResult } from '@tanstack/react-query';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useId } from 'react';

type QueryResult<TData, TError> =
  | UseInfiniteQueryResult<TData, TError>
  | UseSuspenseInfiniteQueryResult<TData, TError>;

interface PaginationProps<TData, TError> {
  query: QueryResult<TData, TError>;
  currentLength: number;
  totalItems?: number;
  className?: string;
  /** A crawlable destination; plain clicks still append products in place. */
  nextPageHref?: string;
  previousPageHref?: string;
}

export function Pagination<
  TData extends {
    pages?: Array<{
      meta?: { total?: number; from?: number; current_page?: number; per_page?: number };
    }>;
  },
  TError,
>({
  query,
  currentLength,
  totalItems,
  className,
  nextPageHref,
  previousPageHref,
}: PaginationProps<TData, TError>) {
  const { t } = useTranslations();
  const countLabelId = useId();
  const total = totalItems ?? query.data?.pages?.[0]?.meta?.total ?? 0;
  const first = query.data?.pages?.[0]?.meta;
  const offset = Math.max(
    0,
    (first?.from ?? ((first?.current_page ?? 1) - 1) * (first?.per_page ?? 0) + 1) - 1,
  );
  const end = Math.min(total, offset + currentLength);
  const progressPercentage = total > 0 ? Math.min(100, (end / total) * 100) : 0;

  return (
    <div className={cn('flex w-full flex-col items-center gap-6', className)}>
      <div className="flex w-full flex-col items-center gap-2">
        <p id={countLabelId} className="text-center text-sm sm:text-base">
          {currentLength > 0 && offset > 0 ? `${offset + 1}–${end}` : currentLength} / {total}
        </p>
        <div className="h-[6px] w-full max-w-[300px] overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-labelledby={countLabelId}
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
      {previousPageHref && (
        <Button variant="outline" asChild>
          <a href={previousPageHref} rel="prev" aria-label={t('text3529')}>
            <span aria-hidden="true">←</span> {t('text3529')}
          </a>
        </Button>
      )}
      {query.hasNextPage &&
        (nextPageHref ? (
          <Button variant="outline" asChild className="min-w-[120px]">
            <a
              href={nextPageHref}
              rel="next"
              aria-label={t('text338')}
              aria-busy={query.isFetchingNextPage}
              onClick={event => {
                if (
                  event.defaultPrevented ||
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                event.preventDefault();
                if (!query.isFetchingNextPage) void query.fetchNextPage();
              }}
            >
              {t('text338')}
            </a>
          </Button>
        ) : (
          <Button
            variant="outline"
            isLoading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
            className="min-w-[120px]"
            aria-label={t('text338')}
          >
            {t('text338')}
          </Button>
        ))}
    </div>
  );
}
